import * as THREE from "three";
import { generateLobbyId, parseLobbyIdFromPath } from "./routing.js";
import { NetClient, partykitHostFromOrigin } from "./net/client.js";
import { LobbyScene } from "./scenes/lobbyScene.js";
import { MatchScene } from "./scenes/matchScene.js";
import { LocalPlayer } from "./entities/localPlayer.js";
import { Keyboard } from "./input/keyboard.js";
import { createPointerLock } from "./input/pointerLock.js";
import { LobbyHud } from "./ui/lobbyHud.js";
import { MatchHud } from "./ui/matchHud.js";
import { Crosshair } from "./render/crosshair.js";
import { makeShieldBubble, disposeShieldBubble } from "./render/shieldBubble.js";
import type { ServerMsg } from "@genjiball/protocol";
import { playSfx, unlockAudio } from "./audio/synth.js";

const lobbyId = parseLobbyIdFromPath(window.location.pathname);
if (!lobbyId) {
  const fresh = generateLobbyId();
  window.location.replace(`/p/${fresh}`);
  throw new Error("redirecting");
}

const canvas = document.getElementById("game") as HTMLCanvasElement;
const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.setSize(window.innerWidth, window.innerHeight, false);
renderer.setClearColor(0x0d1015);
renderer.shadowMap.enabled = true;

const lobbyScene = new LobbyScene();
const matchScene = new MatchScene();
let activeScene: "lobby" | "match" = "lobby";
const camera = new THREE.PerspectiveCamera(70, window.innerWidth / window.innerHeight, 0.1, 200);

const kb = new Keyboard();
const lock = createPointerLock(canvas);

let localPlayer: LocalPlayer | null = null;
let myId: string | null = null;
let myColor: string = "#ffffff";

const net = new NetClient({ host: partykitHostFromOrigin(), room: lobbyId });

const hud = new LobbyHud({
  onReadyToggle: (ready) => net.send({ t: "ready", ready }),
  onStart: () => net.send({ t: "start" }),
  onCopyLink: () => {
    void navigator.clipboard.writeText(window.location.href);
  },
  onAddBot: () => net.send({ t: "addBot" }),
});

const matchHud = new MatchHud();
const crosshair = new Crosshair(document.getElementById("hud")!);
crosshair.setVisible(true);

let shieldBubble: THREE.Mesh | null = null;
let shieldActiveUntil = 0;
let shieldCooldownUntil = 0;
let leftMousePressedEdge = false;

const clickPrompt = document.createElement("div");
clickPrompt.id = "hud-click-prompt";
clickPrompt.textContent = "Click to play";
document.getElementById("hud")!.appendChild(clickPrompt);

document.addEventListener("pointerlockchange", () => {
  clickPrompt.hidden = document.pointerLockElement === canvas;
});
clickPrompt.hidden = false;

canvas.addEventListener("mousedown", (e) => {
  if (e.button !== 0 || !lock.state.locked) return;
  // Respect the client-side cooldown so spam clicks don't trigger SFX,
  // bubble, or kick animations during cooldown (server ignores them anyway).
  if (performance.now() < shieldCooldownUntil) return;
  leftMousePressedEdge = true;
});

canvas.addEventListener("click", () => {
  unlockAudio();
});

let inputSeq = 0;
let lastSendAt = 0;
const SEND_INTERVAL_MS = 50;  // 20 Hz

net.on((msg: ServerMsg) => {
  switch (msg.t) {
    case "welcome": {
      myId = msg.youAreId;
      const mePlayer = msg.lobby.players[myId];
      myColor = mePlayer?.color ?? "#ffffff";
      const spawn = pickSpawn();
      localPlayer = new LocalPlayer(myColor, spawn);
      void localPlayer.loadCharacter(myId);
      // Tell both scenes which player is local so they skip creating a remote
      // copy of self in syncLobby.
      lobbyScene.setLocalPlayerId(myId);
      matchScene.setLocalPlayerId(myId);
      lobbyScene.syncLobby(msg.lobby);
      lobbyScene.scene.add(localPlayer.group);
      net.send({ t: "join", name: `Guest-${myId.slice(0, 4)}` });
      hud.setSelf(myId);
      hud.render(msg.lobby);
      matchHud.setSelf(myId);
      break;
    }
    case "lobby": {
      const wantMatch = msg.lobby.status === "playing";
      activeScene = wantMatch ? "match" : "lobby";
      lobbyScene.syncLobby(msg.lobby);
      matchScene.syncLobby(msg.lobby);
      if (wantMatch && localPlayer && !matchScene.scene.children.includes(localPlayer.group)) {
        matchScene.scene.add(localPlayer.group);
      }
      hud.render(msg.lobby);
      matchHud.syncLobby(msg.lobby);
      break;
    }
    case "snapshot":
      if (activeScene === "match") {
        matchScene.ingestSnapshot(msg.serverTimeMs, msg.players, msg.ball);
      } else {
        lobbyScene.ingestSnapshot(msg.serverTimeMs, msg.players);
      }
      if (msg.ball) {
        const v = msg.ball.vel;
        matchHud.setBallSpeed(Math.hypot(v[0], v[1], v[2]));
      } else {
        matchHud.setBallSpeed(null);
      }
      break;
    case "roundStart": {
      matchHud.onRoundStart(msg);
      shieldActiveUntil = 0;
      shieldCooldownUntil = 0;
      crosshair.setState(msg.targetId === myId ? "targeted" : "default");

      // Schedule 3 beeps + GO based on server's startsAt + 1s intervals.
      const now = Date.now();
      const offsets = [0, 1000, 2000];
      for (const off of offsets) {
        const delay = msg.startsAt + off - now;
        if (delay > 0) setTimeout(() => playSfx("beep"), delay);
      }
      const goDelay = msg.startsAt + 3000 - now;
      if (goDelay > 0) setTimeout(() => playSfx("go"), goDelay);
      break;
    }
    case "kill":
      matchHud.onKill(msg.playerId);
      playSfx("hit");
      break;
    case "roundEnd":
      matchHud.onRoundEnd();
      break;
    case "shieldOk":
      if (msg.playerId === myId) {
        crosshair.setState("shieldActive");
      }
      playSfx("deflect");
      break;
    default:
      break;
  }
});

function pickSpawn(): [number, number, number] {
  return [Math.random() * 6 - 3, 0, Math.random() * 6 - 3];
}

function onResize() {
  renderer.setSize(window.innerWidth, window.innerHeight, false);
  camera.aspect = window.innerWidth / window.innerHeight;
  camera.updateProjectionMatrix();
}
window.addEventListener("resize", onResize);

let lastTimeMs = performance.now();
let lastDebugLog = 0;
let lastWallDebugLog = 0;

function animate() {
  requestAnimationFrame(animate);
  const now = performance.now();
  const dt = Math.min(0.05, (now - lastTimeMs) / 1000);
  lastTimeMs = now;

  if (localPlayer) {
    // If pointer is not locked, skip mouse polling but still allow keyboard movement.
    // (Otherwise stale yawDelta could snap the camera on relock.)
    if (!lock.state.locked) {
      lock.poll(); // drain any accumulated deltas without applying
    } else {
      const { dx, dy } = lock.poll();
      localPlayer.applyMouse(dx, dy);
    }
    localPlayer.step(kb, dt);

    // Lobby phase: falling below the death plane respawns at the platform centre.
    // Match phase: the server handles the kill broadcast; the local player just
    // keeps falling until the kill event resets them.
    if (activeScene === "lobby" && localPlayer.pos[1] < -20) {
      localPlayer.pos = [0, 0, 0];
      localPlayer.vel = [0, 0, 0];
      localPlayer.group.position.set(0, 0, 0);
    }

    // Wall-climb diagnosis: if Space is held in a way that *could* engage a
    // climb (airborne + below the top) but the check is failing, log the
    // reason at most once per second.
    if (
      kb.isDown("Space") &&
      !localPlayer.grounded &&
      localPlayer.pos[1] < 0 &&
      localPlayer.pos[1] > -20 &&
      localPlayer.debugFailReason &&
      now - lastWallDebugLog > 1000
    ) {
      lastWallDebugLog = now;
      console.log(`[wall-fail] ${localPlayer.debugFailReason}`);
    }

    // Third-person orbital camera: focus point sits high above the player
    // (head/over-the-shoulder height). At pitch=0 the camera is directly behind
    // at the same Y. Mouse-up tilts the camera lower; mouse-down raises it.
    const yaw = localPlayer.yaw;
    const pitch = localPlayer.pitch;
    const camDist = 4.5;
    const focusY = localPlayer.pos[1] + 1.5;   // shoulder height of a 1.8m character
    const cp = Math.cos(pitch);
    const sp = Math.sin(pitch);
    const offX = Math.sin(yaw) * cp * camDist;
    const offY = -sp * camDist;
    const offZ = Math.cos(yaw) * cp * camDist;
    camera.position.set(
      localPlayer.pos[0] + offX,
      focusY + offY,
      localPlayer.pos[2] + offZ,
    );
    camera.lookAt(
      localPlayer.pos[0],
      focusY,
      localPlayer.pos[2],
    );

    // One-time-per-second debug log for sanity.
    if (now - lastDebugLog > 1000) {
      lastDebugLog = now;
      console.log(
        `[local] pos=[${localPlayer.pos[0].toFixed(2)}, ${localPlayer.pos[1].toFixed(2)}, ${localPlayer.pos[2].toFixed(2)}]  ` +
          `cam=[${camera.position.x.toFixed(2)}, ${camera.position.y.toFixed(2)}, ${camera.position.z.toFixed(2)}]`,
      );
    }

    // Send input at 20 Hz.
    if (now - lastSendAt >= SEND_INTERVAL_MS) {
      lastSendAt = now;
      inputSeq++;
      net.send({
        t: "input",
        seq: inputSeq,
        pos: [...localPlayer.pos] as [number, number, number],
        yaw: localPlayer.yaw,
        pitch: localPlayer.pitch,
        vel: [...localPlayer.vel] as [number, number, number],
        grounded: localPlayer.grounded,
        shield: leftMousePressedEdge,
      });
      if (leftMousePressedEdge) {
        playSfx("shieldActivate");
        const now = performance.now();
        shieldActiveUntil = now + 500;
        shieldCooldownUntil = now + 700;
        if (localPlayer && !shieldBubble && activeScene === "match") {
          shieldBubble = makeShieldBubble();
          localPlayer.group.add(shieldBubble);
        }
        // Trigger a random kick animation for visual flourish.
        if (localPlayer) {
          const KICKS = [
            "kick_1_martelo",
            "kick_2_mma",
            "kick_3_knee",
            "kick_4_mma",
          ] as const;
          const pick = KICKS[Math.floor(Math.random() * KICKS.length)]!;
          localPlayer.visual.playOnce(pick);
        }
      }
      leftMousePressedEdge = false;
    }
  }

  if (activeScene === "match") {
    matchScene.update(Date.now());
    renderer.render(matchScene.scene, camera);
  } else {
    lobbyScene.update(Date.now());
    renderer.render(lobbyScene.scene, camera);
  }

  if (shieldBubble && performance.now() >= shieldActiveUntil) {
    shieldBubble.parent?.remove(shieldBubble);
    disposeShieldBubble(shieldBubble);
    shieldBubble = null;
  }
  if (activeScene === "match") {
    const t = performance.now();
    if (t < shieldActiveUntil) {
      crosshair.setState("shieldActive");
    } else if (t < shieldCooldownUntil) {
      crosshair.setState("cooldown");
    } else {
      crosshair.setState("default");
    }
  }
}
animate();
